import React, { useState, useRef, useEffect } from 'react';
import { string, array, func } from 'prop-types';
import cx from 'classnames';
import LazyLoad from 'react-lazyload';

import useStyles from 'hooks/useStyles';
import useBaseOnWindowSize from 'hooks/useBaseOnWindowSize';
import useGetCurrentImageType from 'hooks/useGetCurrentImageType';

import Link from 'components/Link';
import BaseSwiper from 'components/BaseSwiper';
import LoadingSkeleton from 'components/LoadingSkeleton';
import LoadedImage from 'components/LoadedImage';

import {
  WIDE_DESKTOP_WIDTH,
  DESKTOP_WIDTH,
  TABLET_WIDTH,
} from 'styles/variables.scss';

import styles from './PromotionBanner.scss';
import commonSwiperStyles from './commonSwiper.scss';

const breakpoints = {
  [WIDE_DESKTOP_WIDTH]: {
    slidesPerView: 2.407,
    slidesPerGroup: 1,
  },
  [DESKTOP_WIDTH]: {
    slidesPerView: 2.433,
    slidesPerGroup: 1,
  },
  [TABLET_WIDTH]: {
    slidesPerView: 2.23,
    slidesPerGroup: 1,
  },
  0: {
    slidesPerView: 1.1,
    slidesPerGroup: 1,
  },
};

const OFFSET_TYPE_LIST = ['immediately', 'default'];
const DEFAULT_OFFSETS = [-100, 0];

const getOffset = (offsetType, index) => {
  if (offsetType === OFFSET_TYPE_LIST[0])
    return [window.innerWidth * 0.6 * index, 0];

  return DEFAULT_OFFSETS;
};

const PromotionBanner = ({ id, bannerList, onClick }) => {
  useStyles(commonSwiperStyles, styles);

  const { imageType, setContainerRef } = useGetCurrentImageType(true);

  const swiperRef = useRef();
  const [shouldSliderCentered, setShouldSliderCentered] = useState(false);
  const [offsetType, setOffsetType] = useState(OFFSET_TYPE_LIST[0]);

  useEffect(() => {
    const handleScroll = () => {
      setOffsetType(OFFSET_TYPE_LIST[1]);
      window.removeEventListener('scroll', handleScroll);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const checkShouldSlicerCentered = ref => {
    if (ref) {
      swiperRef.current = ref;
    }

    if (swiperRef.current) {
      setShouldSliderCentered(
        swiperRef.current?.isBeginning && swiperRef?.current.isEnd,
      );

      setContainerRef(swiperRef.current.el);
    }
  };

  useBaseOnWindowSize(() => checkShouldSlicerCentered());

  const renderSlide = ({ id: sliceId, url, content, image }, index) => (
    <Link
      newTab
      key={sliceId}
      to={url}
      className={styles.slide}
      data-ooo-image-container
      onClick={() => onClick({ id: sliceId, content })}
    >
      <LazyLoad
        height={126}
        offset={getOffset(offsetType, index + 1)}
        placeholder={<LoadingSkeleton className={styles.loading} />}
      >
        <LoadedImage
          className={styles.img}
          alt={content || null}
          src={image[imageType]}
        />
      </LazyLoad>
    </Link>
  );

  if (bannerList.length === 0) {
    return null;
  }

  return (
    <section
      className={cx(styles.root, {
        [styles.swiperCenter]: shouldSliderCentered,
      })}
      id={id}
    >
      <BaseSwiper
        navigation
        onSwiper={checkShouldSlicerCentered}
        pagination={false}
        renderSlide={renderSlide}
        slideDataList={bannerList}
        breakpoints={breakpoints}
        id="class-banner-swiper"
        classNameMap={{
          root: commonSwiperStyles.root,
        }}
      />
    </section>
  );
};

PromotionBanner.propTypes = {
  id: string,
  bannerList: array.isRequired,
  onClick: func.isRequired,
};

PromotionBanner.defaultProps = {
  id: '',
};

export default PromotionBanner;
